import { createFileRoute, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "react-oauth2-code-pkce";

import {
  AlertSeverity,
  Button,
  ButtonVariant,
  ColorVariant,
  Dialog,
  eAnchorTarget,
  eAsidePanelType,
  eCTA,
  IconName,
  Navbar,
  Sidebar,
  useAlert,
  UserBadge,
  type NSAction,
  type tAsidePanelSpec,
} from "@mitratech/north-star";

import { useApp } from "../../app/Application.provider";
import {
  INTEGRATIONS_API_HOME_ROUTE,
  INTEGRATIONS_API_PANEL_ID,
  SUPER_ADMIN_SETTINGS_SCREENS,
} from "../../app/integrationsApi";
import { usePanelConfig } from "../../app/usePanelConfig";
import loaLogo from "../../assets/logo/logo-white.svg";
import { RouteGuard } from "../../auth/guards/RouteGuard";
import {
  getActiveTenantCookie,
  getCurrentTenant,
  hasStoredOAuthToken,
  logoutRouteWithIntent,
  markLogoutIntent,
} from "../../auth/utils";
import { SessionConflictError } from "../../components/auth/SessionConflictError";
import { HelpMenu, type HelpOption } from "../../components/help/HelpMenu";
import { ProductSwitcher } from "../../components/product-switcher";
import { AllCasesDialog } from "../../features/cases/compontents/AllCasesDialog";
import { CaseDialogBroker } from "../../features/cases/compontents/broker/CaseDialogBroker";
import { CaseActionConfirmState } from "../../features/cases/compontents/types";
import { caseConfirmCancelState, caseConfirmCloseState, caseConfirmReopenState } from "../../features/cases/constants";
import { CaseExitGuardRegistryProvider } from "../../features/cases/context/CaseExitGuardRegistry";
import { useStartFreshCase } from "../../features/cases/hooks/useStartFreshCase";
import { useChangeCaseStatus } from "../../features/cases/mutations";
import { caseRouteFor } from "../../features/cases/caseRoute";
import { useCases } from "../../features/cases/queries";
import { caseKeys } from "../../features/cases/queryKeys";
import { toCaseStatusErrorMessage } from "../../features/cases/statusPermissions";
import { NotificationPreferencesDrawer } from "../../features/notifications/NotificationPreferencesDrawer";
import { useMe } from "../../features/users/queries";
import type { MeProfile } from "../../features/users/types";
import { useAuth } from "../../hooks/useAuth";
import { usePendo } from "../../hooks/usePendo";
import { sanitizeUrl, trackEvent } from "../../lib/pendoUtils";
import { queryClient } from "../../lib/queryClient";
import { CaseStatus, type Case } from "../../types/loa";
import { ACTIVE_CASE_STATUSES } from "../../types/loa.utils";

interface HQProductSwitcher {
  showLogoutModal: (logoutURL: string, logoutMethod: string) => void;
}

declare global {
  interface Window {
    HQProductSwitcher?: HQProductSwitcher;
  }
}

const HQ_DOMAIN: string =
  (import.meta.env.VITE_HQ_DOMAIN as string | undefined) ?? "hq.mitratech.com";

/**
 * Constructs HQ profile URL from user's HQ subdomain.
 * Returns null if user doesn't have HQ integration.
 */
function getHQProfileUrl(hqCustomerSubdomain: string | null): string | null {
  if (!hqCustomerSubdomain) {
    return null;
  }

  return `https://${hqCustomerSubdomain}.${HQ_DOMAIN}/accounts/profile`;
}

const USER_BADGE_ID = "user-badge";

export const Route = createFileRoute("/_protected")({
  component: () => (
    <CaseExitGuardRegistryProvider>
      <ProtectedLayout />
    </CaseExitGuardRegistryProvider>
  ),
});

type ConfirmState = CaseActionConfirmState;

/**
 * Looks up a sidebar nav item's `href` across all Navigation-type panels.
 * Returns `undefined` for anything that isn't a genuine internal route (no
 * href, an external/`_blank` target, or a same-page fragment like the
 * "New Case" item's placeholder `#/cases` href) — those are left to their
 * native anchor behavior / existing click handlers instead of being routed.
 */
export function findNavRoutePath(panels: Record<string, tAsidePanelSpec>, navitemId: string): string | undefined {
  for (const panel of Object.values(panels)) {
    if (panel.type !== eAsidePanelType.Navigation) continue;

    const item = panel.config.items.find((navitem) => navitem.id === navitemId);

    if (item?.href?.startsWith("/") && item.target !== eAnchorTarget.Blank) {
      return item.href;
    }
  }

  return undefined;
}

function ProtectedLayout() {
  const { ui, auth, toggleSidebar, setActivePanel, setActiveEntity, setActiveNavItem } = useApp();
  const startFreshCase = useStartFreshCase();
  const { loginInProgress, token: oauthToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const helpTriggerRef = useRef<HTMLButtonElement>(null);
  const [isHelpMenuOpen, setIsHelpMenuOpen] = useState(false);
  const { logout } = useAuth();

  // Single-client session enforcement: detect if user has an active session
  // on a different tenant subdomain. Computed synchronously (not in useEffect)
  // so it blocks rendering before RouteGuard can redirect to login.
  const sessionConflict = useMemo(() => {
    const activeTenant = getActiveTenantCookie();
    const currentTenant = getCurrentTenant();

    if (activeTenant && currentTenant && activeTenant !== currentTenant) {
      return activeTenant;
    }

    return null;
  }, []);

  // case crud modal (confirmation)
  const [confirmState, setConfirmState] = useState<ConfirmState>(() => ({ ...caseConfirmCloseState }));

  // Sidebar case-list filter state — local to the layout, not the URL. The
  // Sidebar persists across every protected route, so this is plain UI state
  // rather than something worth deep-linking. Defaults to the cases still being worked (Open and Approved).
  const [filterTerm, setFilterTerm] = useState("");
  const [statuses, setStatuses] = useState<string[]>(() => ACTIVE_CASE_STATUSES.map(String));
  const [isAllCasesOpen, setIsAllCasesOpen] = useState(false);
  const [isNotificationPreferencesOpen, setIsNotificationPreferencesOpen] = useState(false);
  // Mounted on first open so the drawer only fetches once it has been asked for (LOA-1445).
  const [hasOpenedNotificationPreferences, setHasOpenedNotificationPreferences] = useState(false);

  const panels = usePanelConfig();
  const { data: cases, refetch: refetchCases } = useCases({
    filterTerm,
    statuses,
    enabled: Boolean(auth.token),
  });
  const { mutate: changeCaseStatus } = useChangeCaseStatus();
  const { show: showAlert } = useAlert();
  const { data: me, isLoading: isMeLoading } = useMe({ enabled: Boolean(auth.token) });

  // Initialize Pendo analytics after auth + profile load (LOA-647, LOA-699)
  usePendo();

  // The menu item that opened the drawer unmounts with the menu, so the
  // drawer's own focus restore has nowhere to go; send focus to the badge.
  const handleNotificationPreferencesClose = () => {
    setIsNotificationPreferencesOpen(false);
    requestAnimationFrame(() => {
      document.getElementById(`${USER_BADGE_ID}-trigger`)?.focus();
    });
  };

  const handleUserBadgeAction = (action: NSAction) => {
    if (action.name === "logout") {
      // Try to use HQ logout modal if available, otherwise fall back to direct logout
      if (window.HQProductSwitcher?.showLogoutModal) {
        // LOA-1617: HQ navigates to the logout route itself once its modal is
        // confirmed, so the intent is recorded here, on the click that opens
        // it, and the nonce rides in the URL handed to HQ. Cancelling the
        // modal therefore leaves a marker no other navigation can spend.
        // useAuth.logout() records its own in the fallback branch below.
        window.HQProductSwitcher.showLogoutModal(
          logoutRouteWithIntent(markLogoutIntent()),
          "GET",
        );
      } else {
        logout();
      }
    } else if (action.name === "manageNotifications") {
      setHasOpenedNotificationPreferences(true);
      setIsNotificationPreferencesOpen(true);
    } else if (action.name === "viewProfile") {
      const profileUrl = getHQProfileUrl(me?.hq_customer_subdomain ?? null);

      if (profileUrl) {
        window.open(profileUrl, "_blank", "noopener,noreferrer");
      } else {
        // No HQ account to manage the profile in — use the built-in page.
        void navigate({ to: "/profile" });
      }
    }
  };

  const handleHelpClick = () => {
    setIsHelpMenuOpen((isOpen) => !isOpen);
    trackEvent("help_icon_clicked", { page: sanitizeUrl(location.pathname) });
  };

  const handleHelpClose = useCallback(() => {
    setIsHelpMenuOpen(false);
  }, []);

  const handleHelpOptionClick = (option: HelpOption) => {
    trackEvent("help_option_clicked", {
      option,
      page: sanitizeUrl(location.pathname),
    });
  };

  const userBadge = me ? (
    <div data-pendo-exclude>
      <UserBadge<MeProfile>
        id={USER_BADGE_ID}
        entity={me}
        getInitials={(u) => {
          const first = u.first_name?.[0] ?? "";
          const last = u.last_name?.[0] ?? "";

          return (first + last).toUpperCase() || (u.email[0] ?? "").toUpperCase();
        }}
        menuHeaderLabel={me.email}
        menuHeaderIcon={IconName.AccountCircle}
        actions={[
          { name: "viewProfile", text: "Manage Profile", prefix: IconName.ManageAccounts },
          { name: "manageNotifications", text: "Manage Notifications", prefix: IconName.Notifications },
          { name: "logout", text: "Log Out", prefix: IconName.Logout }
        ]}
        onAction={handleUserBadgeAction}
      />
    </div>
  ) : null;

  // Sync active panel / entity / nav from URL so sidebar reflects navigation.
  useEffect(() => {
    const { pathname } = location;
    const [, first, second] = pathname.split("/");

    if (first === "cases" || first === "cases-version-2") {
      setActivePanel("cases");
      // Unconditional (not `if (second) ...`): landing on bare `/cases` must
      // clear a previously-viewed case's id, not just leave it stale — e.g.
      // via browser back/forward, not only the sidebar "+" CTA.
      setActiveEntity(second ?? "");
    } else if (first === "action-items") {
      const scope = new URLSearchParams(location.searchStr).get("scope");
      const navItemByView: Record<string, string> = {
        overdue: "action-items-overdue",
        unassigned: "action-items-unassigned",
      };

      setActivePanel("action-items");
      setActiveNavItem(
        navItemByView[second ?? ""] ??
          (scope === "all" ? "action-items-all" : "action-items-my"),
      );

      if (!ui.isSidebarOpen) {
        toggleSidebar();
      }
    } else if (first === "dashboard") {
      setActivePanel("dashboard");
      setActiveNavItem(second || "company-hub");
    } else if (first === "settings" && second !== undefined && SUPER_ADMIN_SETTINGS_SCREENS.has(second)) {
      // The Super Admin Settings screens (keys, routes, feature toggles) belong to their own rail panel.
      setActivePanel(INTEGRATIONS_API_PANEL_ID);
      setActiveNavItem(second);
    } else if (first === "settings") {
      setActivePanel("dashboard");
      setActiveNavItem("change-password");
    }
  }, [
    location,
    setActiveEntity,
    setActiveNavItem,
    setActivePanel,
    toggleSidebar,
    ui.isSidebarOpen,
  ]);

  // Sync sidebar CSS class on the root element
  useEffect(() => {
    const root = document.getElementById("root");
    root?.classList.toggle("--expanded", ui.isSidebarOpen);
  }, [ui.isSidebarOpen]);

  // A case the Quick agent created opens on the read-only review page, any
  // other on the editable one. Looked up in the sidebar list, then in the
  // case detail cache (a filtered-out case is not in the list).
  const navigateToCase = (caseId: string, knownCase?: Case) => {
    const known =
      knownCase ??
      cases?.find((candidate) => candidate.id === caseId) ??
      queryClient.getQueryData<Case>(caseKeys.detail(caseId));

    if (caseRouteFor(known) === "/cases-version-2/$caseId") {
      void navigate({ to: "/cases-version-2/$caseId", params: { caseId } });
    } else {
      void navigate({ to: "/cases/$caseId", params: { caseId } });
    }
  };

  const handlePanelClick = (id: string) => {
    // /profile has no rail panel of its own, so the active panel is still
    // whichever one the user came from; clicking it must navigate back
    // rather than treat it as "already there".
    const isOnProfile = location.pathname === "/profile";

    if (!isOnProfile && id === "cases" && id === ui.activePanelId) {
      // Already viewing the cases panel — nothing to navigate to, just
      // bring the sidebar list up to date.
      void refetchCases();

      return;
    }
    if (!isOnProfile && id === ui.activePanelId) return;

    setActivePanel(id);
    if (id === "cases") {
      if (ui.activeEntityId) {
        // A case is already in scope — return to it rather than resetting
        // to the blank slate.
        navigateToCase(ui.activeEntityId);
      } else {
        void navigate({ to: "/cases" });
      }
    } else if (id === INTEGRATIONS_API_PANEL_ID) {
      void navigate({ to: INTEGRATIONS_API_HOME_ROUTE });
    } else {
      void navigate({ to: `/${id}/` as string });
    }
  };

  const handleNavClick = (navitemId: string) => {
    setActiveNavItem(navitemId);

    // Redirect Change Password to HQ profile page
    if (navitemId === "settings") {
      const profileUrl = getHQProfileUrl(me?.hq_customer_subdomain ?? null);

      if (profileUrl) {
        window.open(profileUrl, "_blank", "noopener,noreferrer");
      }

      return;
    }

    // Redirect Users to HQ users page
    if (navitemId === "users") {
      const usersUrl = me?.hq_customer_subdomain
        ? `https://${me.hq_customer_subdomain}.${HQ_DOMAIN}/users`
        : null;

      if (usersUrl) {
        window.open(usersUrl, "_blank", "noopener,noreferrer");
      }

      return;
    }

    const routePath = findNavRoutePath(panels, navitemId);

    if (routePath) {
      void navigate({ to: routePath });
    }
  };

  const handleEntityClick = (id: string, caseEntity?: Case) => {
    setActivePanel("cases");
    setActiveEntity(id);
    navigateToCase(id, caseEntity);
  };

  const handleViewAllCasesDetails = (id: string, caseEntity?: Case) => {
    setIsAllCasesOpen(false);
    handleEntityClick(id, caseEntity);
  };

  const handleEntityFilter = (newTerm = "") => {
    // EntityPanel calls onEntityFilter during its render to sync its internal
    // state with the filterTerm prop. Guard against that render-phase call to
    // avoid a redundant state update while React is rendering.
    if (newTerm === filterTerm) return;
    setFilterTerm(newTerm);
  };

  const handleFilterChange = (newStatuses: string[]) => {
    setStatuses(newStatuses);
  };

  const handleCreateCaseRequest = (panelId: string) => {
    if (panelId !== "cases") return;

    startFreshCase();
  };

  const handleCaseCloseRequest = (caseId: string) => {
    setConfirmState({ ...caseConfirmCloseState, caseId, isOpen: true });
  };

  const handleCaseCancelRequest = (caseId: string) => {
    setConfirmState({ ...caseConfirmCancelState, caseId, isOpen: true });
  };

  const handleCaseReopenRequest = (caseId: string) => {
    setConfirmState({ ...caseConfirmReopenState, caseId, isOpen: true });
  };

  const handleCasesContextMenuAction = (panelId: string, entityId: string, actionId: string) => {
    if (panelId === "cases") {
      switch (actionId) {
        case "case-close-request":
          handleCaseCloseRequest(entityId);
          break;
        case "case-cancel-request":
          handleCaseCancelRequest(entityId);
          break;
        case "case-reopen-request":
          handleCaseReopenRequest(entityId);
          break;
      }
    }
  };

  // Never a silent failure: the server refuses non-owners with 403, and a
  // stale owner or illegal transition can land here even with the menu gated.
  const requestStatusChange = (
    caseId: string | undefined,
    status: CaseStatus,
    doneTitle: string,
  ) => {
    if (!caseId) {
      return;
    }

    changeCaseStatus(
      { id: caseId, payload: { status } },
      {
        onSuccess: () => {
          showAlert({ title: doneTitle, severity: AlertSeverity.Success });
        },
        onError: (error) => {
          console.error("Failed to change case status", error);
          showAlert({
            title: "Could not change case status",
            description: toCaseStatusErrorMessage(error),
            severity: AlertSeverity.Error,
          });
          // The list may hold a stale owner or status; pick up the current one.
          void refetchCases();
        },
      },
    );
  };

  const handleDialogAction = (action: NSAction) => {
    const { caseId } = confirmState;
    switch (action.name) {
      case "case-close-confirm":
        requestStatusChange(caseId, CaseStatus.Closed, "Case closed");
        setConfirmState({ ...caseConfirmCloseState, isOpen: false });
        break;

      case "case-cancel-confirm":
        requestStatusChange(caseId, CaseStatus.Cancelled, "Case cancelled");
        setConfirmState({ ...caseConfirmCancelState, isOpen: false });
        break;

      case "case-open-confirm":
        requestStatusChange(caseId, CaseStatus.Open, "Case reopened");
        setConfirmState({ ...caseConfirmReopenState, isOpen: false });
        break;

      default:
        setConfirmState({ ...confirmState, isOpen: false });
    }
  };

  // Single-client session enforcement: if user has active session on different
  // tenant, show conflict error and require logout before proceeding.
  if (sessionConflict) {
    return <SessionConflictError />;
  }

  // While the OAuth library has a token in storage but OAuthBridge hasn't dispatched
  // AUTH_LOGIN yet, show a loading indicator to prevent a premature redirect to /auth/login.
  // Also check localStorage directly - the library may not have read the token yet
  // (e.g., user opened a new tab with a direct URL while already logged in).
  const isInitializing =
    !auth.isAuthenticated && (!!oauthToken || hasStoredOAuthToken()) && !loginInProgress;

  if (loginInProgress || isInitializing) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <div
          style={{
            width: 40,
            height: 40,
            border: "3px solid #f3f3f3",
            borderTop: "3px solid #3498db",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <RouteGuard isAuthenticated={auth.isAuthenticated} user={auth.user}>
      <Navbar
        ctaControls='help-menu'
        ctaExpanded={isHelpMenuOpen}
        ctaRef={helpTriggerRef}
        logo={<img alt='Mitratech logo' className='mitra-logo' src={loaLogo} />}
        cta={{
          type: eCTA.Link,
          config: {
            id: "help",
            icon: IconName.Help,
            label: "How can we help?",
            text: "Help",
            url: "#",
            target: eAnchorTarget.Self,
          },
          display: true,
        }}
        onCallToActionClick={handleHelpClick}
        onToggle={toggleSidebar}
        additionalActions={!isMeLoading && me && (
          <ProductSwitcher
            hqUserId={me.hq_user_id ?? null}
            hqCustomerSubdomain={me.hq_customer_subdomain ?? null}
          />
      )}
      />
      <HelpMenu
        anchorRef={helpTriggerRef}
        isOpen={isHelpMenuOpen}
        onClose={handleHelpClose}
        onOptionClick={handleHelpOptionClick}
      />
      <Sidebar
        filterTerm={filterTerm}
        activePanelId={ui.activePanelId}
        activeEntityId={ui.activeEntityId ?? ""}
        activeNavitemId={ui.activeNavitemId}
        isExpanded={ui.isSidebarOpen}
        panels={panels}
        entities={{ cases: cases ?? [] }}
        onToggle={toggleSidebar}
        onPanelClick={handlePanelClick}
        onEntityClick={handleEntityClick}
        onNavClick={handleNavClick}
        onPanelCtaClick={handleCreateCaseRequest}
        onEntityFilter={handleEntityFilter}
        selectedFilterValues={statuses}
        onFilterChange={handleFilterChange}
        onEntityContextMenuAction={handleCasesContextMenuAction}
        userBadge={userBadge}
        panelFooter={
          ui.activePanelId === "cases" ? (
            <Button
              label="View All Cases"
              text="View All Cases"
              suffix={IconName.ChevronRight}
              variant={ButtonVariant.Text}
              color={ColorVariant.Primary}
              onClick={() => setIsAllCasesOpen(true)}
            />
          ) : undefined
        }
      />
      {hasOpenedNotificationPreferences && (
        <NotificationPreferencesDrawer
          isOpen={isNotificationPreferencesOpen}
          onClose={handleNotificationPreferencesClose}
        />
      )}
      <AllCasesDialog
        isOpen={isAllCasesOpen}
        onClose={() => setIsAllCasesOpen(false)}
        onViewDetails={handleViewAllCasesDetails}
      />
      <main>
        <Outlet />
        <Dialog
          {...confirmState}
          onClose={() => setConfirmState({ ...confirmState, isOpen: false })}
          onAction={handleDialogAction}
        >
          <CaseDialogBroker command={confirmState.command} />
        </Dialog>
      </main>
    </RouteGuard>
  );
}
